import React, { useEffect, useState } from 'react';
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation
} from 'react-router-dom';
import LoginForm from './scenes/login';
import Topbar from './scenes/global/Topbar';
import Sidebar from './scenes/global/Sidebar';
import Dashboard from './scenes/dashboard';
import { ColorModeContext, useMode } from './theme';
import { CssBaseline, ThemeProvider } from '@mui/material';
import LoadingScreen from './components/LoadingScreen';
import ResetPasswordForm from './scenes/resetPass/ResetPasswordForm';
// Import i18n
import { I18nextProvider } from 'react-i18next';
import i18n from './translations/i18n'; // Adjust the path if necessary

import Students from './scenes/students';
import Complaints from './scenes/complaints';
import StatisticsByClass from './scenes/statistics/StatResultByClass';
import StatisticsByGender from './scenes/statistics/StatResultByGender';
import StatisticsByLanguage from './scenes/statistics/StatByLanguage';
import ProfilePage from './scenes/profile';
import CreateComplaint from './scenes/complaints/createComplaint';
import SchoolResults from './scenes/students/result';
import AdminStudents from './scenes/students/administration/manageStudents';
import AdminModes from './scenes/competitionModes/AdminModes';
import AdminSchools from './scenes/schools/administration/manageSchools';
import LocationManager from './scenes/locations/administration/locationManager';
import CategoriesPage from './scenes/categories/administration';
import AdminStatResult from './scenes/statistics/admin/StatByResultDepend';
import ResultsByGrade from './scenes/statistics/admin/StatForAllStudentClass';
import ComplaintsAdminPage from './scenes/complaints/administration';
import UsersAdministration from './scenes/users/administration';
import RolesPage from './scenes/roles/administration';
import DiplomaTemplates from './scenes/diplomaTemplates';
import DiplomasBySchool from './scenes/diplomasBySchool';
import ReviewPage from './scenes/review/Review';
import StudentDetails from './scenes/review/reviewLastStep - Check';
import ClassStudents from './scenes/review/reviewLastStep - Check/classStudents';
import ReviewComponent from './scenes/review/reviewProgress';
import GradeSolutionsManager from './scenes/solutions';
import FinalGradeSolutionsManager from 'src/scenes/finalSolutions';
import StepperComponent from './scenes/review/stteperComponents';
import ConnectWorkPage from './scenes/review/connectionWork/ConnectWorkPage';
import ReviewBySchoolPage from './scenes/review/checkReviewSchool/ReviewSchoolsPage';
import SchoolStudents from './scenes/review/checkReviewSchool/SchoolStudent';
import VerifyReviews from './scenes/review/CheckReviewBoth/CheckReviewBoth';
import SchoolPay from './scenes/DMS/schoolPay';
import { jwtDecode } from 'jwt-decode';
import FinalResultsByGrade from './scenes/finalStatistics/admin/StatForAllStudentClass';
import FinalVerifyReviews from './scenes/finalReview/CheckReviewBoth/CheckReviewBoth';
import FinalReviewPage from './scenes/finalReview/Review';
import FinalClassStudents from './scenes/finalReview/reviewLastStep - Check/classStudents';
import FinalStepperComponent from './scenes/finalReview/stteperComponents';
import FinalConnectWorkPage from './scenes/finalReview/connectionWork/ConnectWorkPage';
import FinalReviewComponent from './scenes/finalReview/reviewProgress';
import FinalSchoolStudents from './scenes/finalReview/checkReviewSchool/SchoolStudent';
import SchoolDiplomasPage from './scenes/schoolDiplomasPage';
import DiplomaVerifyPage from './scenes/diplomaVerifyPage';

function App() {
  const [theme, colorMode] = useMode();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const DMS_ROLE = 1;

  const handleLogin = (token) => {
    const decodedToken = jwtDecode(token);
    setIsLoggedIn(true);
    setUserRole(decodedToken.role);
    localStorage.setItem('token', token);

    // Redirect based on role
    if (decodedToken.role === DMS_ROLE) {
      navigate('/pay');
    } else {
      navigate('/');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserRole(null);
    localStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setIsLoggedIn(true);
      setUserRole(decodedToken.role);
    }
    setIsChecking(false);
  }, []);

  if (isChecking || (isLoggedIn && userRole === null)) {
    return <LoadingScreen />;
  }

  // Definišite rute koje ne zahtevaju Sidebar i Topbar
  const noLayoutRoutes = ['/login', '/reset-password', '/verify'];

  // Proverite da li je trenutna ruta u noLayoutRoutes
  const isNoLayoutRoute = noLayoutRoutes.includes(location.pathname);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="app">
          {/* Uslovno renderovanje Sidebar-a */}
          {isLoggedIn && !isNoLayoutRoute && (
            <Sidebar
              className="sidebar"
              onLogout={handleLogout}
              userRole={userRole}
            />
          )}
          <main className="content">
            {/* Uslovno renderovanje Topbar-a */}
            {isLoggedIn && !isNoLayoutRoute && (
              <Topbar onLogout={handleLogout} />
            )}
            <Routes>
              {/* Javni Putovi */}
              <Route
                path="/login"
                element={
                  isLoggedIn ? (
                    <Navigate to="/" replace />
                  ) : (
                    <LoginForm onLogin={handleLogin} />
                  )
                }
              />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/verify" element={<DiplomaVerifyPage />} />

              {/* Zaštićeni Putovi */}
              {isLoggedIn ? (
                userRole === '11' ? (
                  // Rute za DMS ulogu (uloga '11')
                  <>
                    <Route path="/pay" element={<SchoolPay />} />
                    <Route path="*" element={<Navigate to="/pay" replace />} />
                  </>
                ) : userRole === '3' ? (
                  // Rute za školsku ulogu (uloga '3')
                  <>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/ProfilePage" element={<ProfilePage />} />
                    <Route
                      path="/all-students/:schoolIdRef"
                      element={<Students />}
                    />
                    <Route
                      path="/complaints/:schoolIdRef"
                      element={<Complaints />}
                    />
                    <Route
                      path="/statisticsByClass"
                      element={<StatisticsByClass />}
                    />
                    <Route
                      path="/createComplaint"
                      element={<CreateComplaint />}
                    />
                    <Route
                      path="/statisticByGender"
                      element={<StatisticsByGender />}
                    />
                    <Route
                      path="/statisticByLanguage"
                      element={<StatisticsByLanguage />}
                    />
                    <Route path="/schoolResults" element={<SchoolResults />} />
                    <Route
                      path="/school/:schoolId"
                      element={<SchoolStudents />}
                    />
                    <Route path="/school-diplomas" element={<SchoolDiplomasPage />} />
                    {/* Preusmeravanje nepoznatih ruta na početnu */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                ) : (
                  // Rute za Admin i druge uloge
                  <>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/students" element={<Students />} />
                    <Route path="/manageStudent" element={<AdminStudents />} />
                    <Route path="/competition-modes" element={<AdminModes />} />
                    <Route path="/schools" element={<AdminSchools />} />
                    <Route path="/locations" element={<LocationManager />} />
                    <Route path="/categories" element={<CategoriesPage />} />
                    <Route
                      path="/statisticsByClass"
                      element={<StatisticsByClass />}
                    />
                    <Route
                      path="/statisticByGender"
                      element={<StatisticsByGender />}
                    />
                    <Route
                      path="/admin-stat-result"
                      element={<AdminStatResult />}
                    />
                    <Route
                      path="/results-by-grade"
                      element={<ResultsByGrade />}
                    />
                    <Route
                      path="/final-results-by-grade"
                      element={<FinalResultsByGrade />}
                    />
                    <Route
                      path="/complaints-admin-page"
                      element={<ComplaintsAdminPage />}
                    />
                    <Route path="/users" element={<UsersAdministration />} />
                    <Route path="/review-1" element={<ReviewPage />} />
                    <Route
                      path="/student-details/:studentId"
                      element={<StudentDetails />}
                    />
                    <Route
                      path="/statisticByLanguage"
                      element={<StatisticsByLanguage />}
                    />
                    <Route
                      path="/class/:classNumber"
                      element={<ClassStudents />}
                    />
                    <Route
                      path="/final-class/:classNumber"
                      element={<FinalClassStudents />}
                    />
                    <Route path="/roles" element={<RolesPage />} />
                    <Route path="/diploma-templates" element={<DiplomaTemplates />} />
                    <Route path="/diplomas-by-school" element={<DiplomasBySchool />} />
                    <Route
                      path="/manage-solutions"
                      element={<GradeSolutionsManager />}
                    />
                    <Route
                      path="/manage-final-solutions"
                      element={<FinalGradeSolutionsManager />}
                    />
                    <Route
                      path="/review-schools"
                      element={<StepperComponent />}
                    />
                    <Route
                      path="/final-review-schools"
                      element={<FinalStepperComponent />}
                    />
                    <Route
                      path="/review-schools-students"
                      element={<ReviewComponent />}
                    />
                    <Route
                      path="/final-review-schools-students"
                      element={<FinalReviewComponent />}
                    />
                    <Route
                      path="/connect-work/:schoolId"
                      element={<ConnectWorkPage />}
                    />
                    <Route
                      path="/final-connect-work/:classId"
                      element={<FinalConnectWorkPage />}
                    />
                    <Route
                      path="/verify-reviews"
                      element={<ReviewPage />}
                    />
                    <Route
                      path="/final-verify-reviews"
                      element={<FinalReviewPage />}
                    />
                    <Route
                      path="/schools-review-check"
                      element={<ReviewBySchoolPage />}
                    />
                    <Route
                      path="/verify-all"
                      element={<VerifyReviews />}
                    />
                    <Route
                      path="/final-verify-all"
                      element={<FinalVerifyReviews />}
                    />
                    <Route
                      path="/school/:schoolId"
                      element={<SchoolStudents />}
                    />
                    {/* Preusmeravanje nepoznatih ruta na početnu */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                )
              ) : (
                // Preusmeravanje na login ako korisnik nije autentifikovan
                <Route path="*" element={<Navigate to="/login" replace />} />
              )}
            </Routes>
          </main>
        </div>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
